// src/pages/ProfileEditPage/HistoryForm/HistoryForm.jsx

"use client";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useOutletContext } from "react-router-dom";
import { Spin, message } from "antd";
import { useTranslation } from "react-i18next";

import { useHistoryForm } from "@/Profile/hooks/useHistoryForm";
import { selectEffectiveUserId } from "../../../../sous-profile/redux/selectors/sousProfileSelectors";
import { triggerProfileUpdate } from "@/utils/profileCompletion";

import "./HistoryForm.css";

/* ===== Icons ===== */
const UserOutlineIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <circle cx="12" cy="7.5" r="3.2" />
    <path d="M4 19.5c0-3.6 3.6-6.5 8-6.5s8 2.9 8 6.5" />
  </svg>
);

const AllergyHandIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="19" viewBox="0 0 18 19" fill="none" aria-hidden>
    <path
      d="M4.20615 14.8359C4.2368 14.9107 4.27084 14.9858 4.30402 15.0586C4.30402 15.0592 4.30487 15.06 4.30487 15.06V15.0606C4.65952 15.8562 5.18349 16.5827 5.78255 17.1092C6.39568 17.6512 7.0403 17.9375 7.64696 17.9375H11.4649C12.336 17.9375 13.1696 17.5972 13.8752 16.9537C14.2501 16.6131 14.5758 16.1963 14.8422 15.7153C15.3422 14.8218 15.6063 13.7666 15.6063 12.6641V4.48531C15.6063 4.08931 15.4494 3.71666 15.1676 3.43906C14.8889 3.15641 14.5165 3.00087 14.1185 3.00087C13.7692 3.00087 13.4475 3.12181 13.1932 3.32347V2.97303C13.1932 2.57281 13.0374 2.19931 12.7578 1.92369C12.4856 1.64722 12.1031 1.48859 11.7088 1.48859C11.2948 1.48859 10.9199 1.65819 10.6499 1.93128C10.577 1.77209 10.4752 1.62641 10.3481 1.50069C10.0736 1.22225 9.69109 1.0625 9.29902 1.0625C8.54752 1.0625 7.92427 1.61994 7.82246 2.34247C7.55752 2.12619 7.22902 2.00891 6.88252 2.00891C6.0638 2.00891 5.39809 2.67491 5.39809 3.49334V8.71897L5.08815 8.09206C4.84487 7.60944 4.35605 7.30963 3.8124 7.30963C3.47124 7.30963 3.15512 7.42494 2.89974 7.64234C2.46184 8.0105 2.2883 8.6 2.45734 9.14338L4.20559 14.8356L4.20615 14.8359Z"
      fill="currentColor"
    />
    <path
      d="M12.7471 12.7931c.5152 0 .9343-.4191.9343-.9343s-.4191-.9343-.9343-.9343-.9346.419-.9346.9343.4194.9343.9346.9343Z"
      fill="currentColor"
    />
  </svg>
);

const SurgicalScalpelIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M3 18h6l5-5-3-3-8 8z" />
    <path d="M12.5 9.5l5.5-5.5a2.5 2.5 0 0 1 3.5 3.5L16 13" />
    <path d="M3 20h8" strokeWidth="1.2" />
  </svg>
);

const MedicalFamilyHistoryIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="19" viewBox="0 0 18 19" fill="none" aria-hidden>
    <path
      d="M16.66 18.5H4.24C1.55 18.08-.26 15.41.42 12.78c.52-2.03 1.86-3.22 3.92-3.61V2.18C4.34 1.18 5 0.5 5.93 0.5h7.88c.3 0 .49.08.69.28L17.75 4.07c.17.17.25.36.25.61v12.1c-.02.72-.54 1.3-1.34 1.52z"
      fill="currentColor"
    />
  </svg>
);

const ChronicConditionsIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="29"
    height="29"
    viewBox="0 0 18 19"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M18 16.44v-13.83C18 1.59 17.17.5 16.05.5H1.94C.82.5 0 1.58 0 3.08V15.92C0 17.42.82 18.5 1.94 18.5h14.12c1.12 0 1.94-1.08 1.94-2.06Z" />
    <path d="M5.61 11.5H4.18c-1.22.01-1.78.77-1.79 2.48 0 .3.01.59-.01.89-.01.31-.16.52-.37.52" />
    <path d="M7.5 6.99c0 1.38-.83 2.51-1.87 2.51s-1.87-1.13-1.87-2.5c0-1.37.83-2.48 1.86-2.5h.08c1.03.02 1.88 1.15 1.8 2.49Z" />
    <path d="M13.13 14.5h2.13M10.9 5.51h4.44M10.94 9.5h4.38" />
  </svg>
);

const PlusIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
    <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.8" />
    <path d="M12 8v8M8 12h8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const TrashIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
    <path
      d="M4 7h16M9 7V5h6v2M6 7l1 13h10l1-13"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

/* ===== Toggle ===== */
const Toggle = ({ checked, onChange }) => (
  <button
    type="button"
    className={`hf-toggle ${checked ? "is-on" : ""}`}
    onClick={() => onChange(!checked)}
    aria-pressed={checked}
  >
    <span />
  </button>
);

/* ===== Card ===== */
function HfCard({ icon, title, onAdd, headerRight, children, addAriaLabel }) {
  return (
    <div className="hf-card" role="region" aria-label={title}>
      <div className="hf-card__header">
        <div className="hf-card__title">
          <span className="hf-ticon-wrap">{icon}</span>
          <span className="hf-card__title-text">{title}</span>
        </div>
        <div className="hf-card__actions">
          {headerRight}
          {onAdd && (
            <button type="button" className="hf-iconbtn" onClick={onAdd} aria-label={addAriaLabel || "Add"}>
              <PlusIcon />
            </button>
          )}
        </div>
      </div>
      <div className="hf-card__body">{children}</div>
    </div>
  );
}

/* ===== Main Component ===== */
export default function HistoryForm() {
  const { t } = useTranslation();
  const { registerSave } = useOutletContext();
  const userId = useSelector(selectEffectiveUserId);

  const {
    lifestyle,
    setLifestyle,
    allergies,
    setAllergies,
    surgeries,
    setSurgeries,
    familyHistory,
    setFamilyHistory,
    chronic,
    setChronic,
    loading,

    saveLifestyle,
    setLifestyleActive,

    saveAllergy,
    updateAllergyBackend,
    deleteAllergy,

    saveSurgery,
    updateSurgeryBackend,
    deleteSurgery,

    saveFamilyHistory,
    updateFamilyHistoryBackend,
    deleteFamilyHistory,

    saveChronicConditions,
  } = useHistoryForm(userId);

  const [dirtyAllergies, setDirtyAllergies] = useState(new Set());
  const [dirtySurgeries, setDirtySurgeries] = useState(new Set());
  const [dirtyFamily, setDirtyFamily] = useState(new Set());

  useEffect(() => {
    if (!registerSave) return;

    const handleFormSave = async () => {
      try {
        let saveCount = 0;

        for (const allergy of allergies) {
          const payload = allergy.model === "surgicalHistory"
            ? {
                type: "ALLERGY",
                item: allergy.name,
                reaction: allergy.reactionDescription,
              }
            : {
                category: allergy.category,
                severityLevel: allergy.severityLevel,
                ...(allergy.category === "DRUG"
                  ? {
                      medicationName: allergy.name,
                      reactionDescription: allergy.reactionDescription,
                    }
                  : allergy.category === "FOOD"
                    ? { foodName: allergy.name }
                    : { allergen: allergy.name }),
              };

          if (allergy?.backendId) {
            if (dirtyAllergies.has(allergy.id)) {
              await updateAllergyBackend(allergy.backendId, payload, allergy.model);
              saveCount++;
            }
          } else {
            const created = await saveAllergy(allergy);

            if (created?.id || created?._id || created?.backendId) {
              const newBackendId = created.backendId || created.id || created._id;
              setAllergies((prev) =>
                prev.map((a) =>
                  a.id === allergy.id ? { ...a, backendId: newBackendId, isNew: false } : a
                )
              );
            }
            saveCount++;
          }
        }

        for (const surgery of surgeries) {
          const payload = {
            type: "SURGERY",
            item: surgery.type,
            date: surgery.year,
          };

          if (surgery?.backendId) {
            if (dirtySurgeries.has(surgery.id)) {
              await updateSurgeryBackend(surgery.backendId, payload);
              saveCount++;
            }
          } else {
            const created = await saveSurgery({
              id: surgery.id,
              type: surgery.type,
              year: surgery.year,
            });

            if (created?.id || created?._id || created?.backendId) {
              const newBackendId = created.backendId || created.id || created._id;
              setSurgeries((prev) =>
                prev.map((s) =>
                  s.id === surgery.id ? { ...s, backendId: newBackendId, isNew: false } : s
                )
              );
            }
            saveCount++;
          }
        }

        for (const family of familyHistory) {
          const payload = {
            relation: family.relation,
            condition: family.condition,
          };

          if (family?.backendId) {
            if (dirtyFamily.has(family.id)) {
              await updateFamilyHistoryBackend(family.backendId, payload);
              saveCount++;
            }
          } else {
            const created = await saveFamilyHistory({
              id: family.id,
              condition: family.condition,
              relation: family.relation,
            });

            if (created?.id || created?._id || created?.backendId) {
              const newBackendId = created.backendId || created.id || created._id;
              setFamilyHistory((prev) =>
                prev.map((f) =>
                  f.id === family.id ? { ...f, backendId: newBackendId, isNew: false } : f
                )
              );
            }
            saveCount++;
          }
        }

        await saveChronicConditions(chronic);

        setDirtyAllergies(new Set());
        setDirtySurgeries(new Set());
        setDirtyFamily(new Set());

        triggerProfileUpdate();

        if (saveCount > 0) {
          message.success(t("historyForm.messages.saved", { count: saveCount }));
        } else {
          message.info(t("historyForm.messages.noChanges"));
        }

        return Promise.resolve();
      } catch (error) {
        console.error("HistoryForm save error:", error);
        message.error(t("historyForm.messages.saveFailed"));
        throw error;
      }
    };

    registerSave(handleFormSave);
  }, [
    registerSave,
    allergies,
    surgeries,
    familyHistory,
    chronic,
    dirtyAllergies,
    dirtySurgeries,
    dirtyFamily,
    saveAllergy,
    saveSurgery,
    saveFamilyHistory,
    saveChronicConditions,
    updateAllergyBackend,
    updateSurgeryBackend,
    updateFamilyHistoryBackend,
    setAllergies,
    setSurgeries,
    setFamilyHistory,
    t,
  ]);

  const toneFromValue = (v) => {
    const val = String(v || "").toLowerCase();
    if (["normal", "mild", "never", "occasionally"].includes(val)) return "success";
    if (["mild reaction", "moderate"].includes(val)) return "warning";
    if (["severe", "life_threatening", "daily"].includes(val)) return "danger";
    if (val === "weekly") return "warning";
    return "success";
  };

  const flashOff = (setter, id) =>
    setTimeout(() => {
      setter((arr) => arr.map((x) => (x.id === id ? { ...x, isNew: false } : x)));
    }, 1200);

  /* ========== LIFESTYLE ========== */
  const updateLifestyle = (id, patch) => {
    setLifestyle((a) => {
      const updated = a.map((x) => (x.id === id ? { ...x, ...patch } : x));
      const item = updated.find((x) => x.id === id);

      if (item && (patch.level !== undefined || patch.label !== undefined)) {
        saveLifestyle(item).then(() => {
          triggerProfileUpdate();
        });
      }

      return updated;
    });
  };

  /* ========== ALLERGY ========== */
  const addAllergy = () => {
    const id = Date.now() + Math.floor(Math.random() * 1000);
    const newAllergy = {
      id,
      name: t("historyForm.defaults.newAllergen"),
      category: "DRUG",
      reactionDescription: "",
      severityLevel: "MILD",
      model: "allergy",
      isNew: true,
    };

    setAllergies((prev) => [...prev, newAllergy]);
    setDirtyAllergies((prev) => new Set(prev).add(id));
    flashOff(setAllergies, id);
  };

  const removeAllergy = (id) => {
    const item = allergies.find((a) => a.id === id);

    if (item?.backendId) {
      deleteAllergy(item.backendId, item.model);
    }

    setAllergies((prev) => prev.filter((x) => x.id !== id));
    setDirtyAllergies((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const updateAllergy = (id, patch) => {
    setAllergies((prev) => prev.map((x) => (x.id === id ? { ...x, ...patch } : x)));
    setDirtyAllergies((prev) => new Set(prev).add(id));
  };

  /* ========== SURGERY ========== */
  const addSurgery = () => {
    const id = Date.now() + Math.floor(Math.random() * 1000);
    const newSurgery = {
      id,
      type: t("historyForm.defaults.surgery"),
      year: t("historyForm.defaults.year"),
      isNew: true,
    };

    setSurgeries((prev) => [...prev, newSurgery]);
    setDirtySurgeries((prev) => new Set(prev).add(id));
    flashOff(setSurgeries, id);
  };

  const removeSurgery = (id) => {
    const item = surgeries.find((s) => s.id === id);

    if (item?.backendId) {
      deleteSurgery(item.backendId);
    }

    setSurgeries((prev) => prev.filter((x) => x.id !== id));
    setDirtySurgeries((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const updateSurgery = (id, patch) => {
    setSurgeries((prev) => prev.map((x) => (x.id === id ? { ...x, ...patch } : x)));
    setDirtySurgeries((prev) => new Set(prev).add(id));
  };

  /* ========== FAMILY ========== */
  const addFamily = () => {
    const id = Date.now() + Math.floor(Math.random() * 1000);
    const newFamily = {
      id,
      condition: t("historyForm.defaults.condition"),
      relation: t("historyForm.defaults.relation"),
      isNew: true,
    };

    setFamilyHistory((prev) => [...prev, newFamily]);
    setDirtyFamily((prev) => new Set(prev).add(id));
    flashOff(setFamilyHistory, id);
  };

  const removeFamily = (id) => {
    const item = familyHistory.find((f) => f.id === id);

    if (item?.backendId) {
      deleteFamilyHistory(item.backendId);
    }

    setFamilyHistory((prev) => prev.filter((x) => x.id !== id));
    setDirtyFamily((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const updateFamily = (id, patch) => {
    setFamilyHistory((prev) => prev.map((x) => (x.id === id ? { ...x, ...patch } : x)));
    setDirtyFamily((prev) => new Set(prev).add(id));
  };

  /* ========== CHRONIC ========== */
  const addChronic = () => {
    const id = Date.now() + Math.floor(Math.random() * 1000);
    const newChronic = {
      id,
      condition: t("historyForm.defaults.condition"),
      diagnosis: t("historyForm.defaults.year"),
      isNew: true,
    };

    setChronic((prev) => [...prev, newChronic]);
    flashOff(setChronic, id);
  };

  const removeChronic = (id) => {
    setChronic((prev) => prev.filter((x) => x.id !== id));
  };

  const updateChronic = (id, patch) => {
    setChronic((prev) => prev.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" tip={t("historyForm.loading")} />
      </div>
    );
  }

  return (
    <div className="hf-rows">
      {/* LIFESTYLE */}
      <div className="hf-row">
        <div className="hf-leftside">
          <h3 className="hf-bullet">{t("historyForm.lifestyle.leftTitle")}</h3>
          <p className="hf-desc">{t("historyForm.lifestyle.leftDesc")}</p>
        </div>
        <div className="hf-rightside">
          <HfCard icon={<UserOutlineIcon />} title={t("historyForm.lifestyle.cardTitle")}>
            <div className="hf-table hf-table--lifestyle">
              <div className="hf-th">
                <span>{t("historyForm.lifestyle.headers.factor")}</span>
                <span>{t("historyForm.lifestyle.headers.level")}</span>
                <span>{t("historyForm.lifestyle.headers.status")}</span>
              </div>

              {lifestyle.map((row) => (
                <div key={row.id} className={`hf-tr ${row.isNew ? "is-new" : ""}`}>
                  <div className="hf-td">
                    <span className="hf-dot" />
                    <input
                      className="hf-input hf-input--editable"
                      value={row.label}
                      onChange={(e) => updateLifestyle(row.id, { label: e.target.value })}
                    />
                  </div>

                  <div className="hf-td">
                    <div className={`hf-sel hf-sel--${toneFromValue(row.level)}`}>
                      <select
                        className="hf-select"
                        value={row.level}
                        onChange={(e) => updateLifestyle(row.id, { level: e.target.value })}
                      >
                        <option value="Occasionally">{t("historyForm.levels.occasionally")}</option>
                        <option value="Daily">{t("historyForm.levels.daily")}</option>
                        <option value="Weekly">{t("historyForm.levels.weekly")}</option>
                        <option value="Never">{t("historyForm.levels.never")}</option>
                      </select>
                    </div>
                  </div>

                  <div className="hf-td hf-td--end">
                    <Toggle checked={row.active} onChange={(v) => setLifestyleActive(row.id, v)} />
                  </div>
                </div>
              ))}
            </div>
          </HfCard>
        </div>
      </div>

      {/* ALLERGY */}
      <div className="hf-row">
        <div className="hf-leftside">
          <h3 className="hf-bullet">{t("historyForm.allergy.leftTitle")}</h3>
          <p className="hf-desc">{t("historyForm.allergy.leftDesc")}</p>
        </div>
        <div className="hf-rightside">
          <HfCard
            icon={<AllergyHandIcon />}
            title={t("historyForm.allergy.cardTitle")}
            onAdd={addAllergy}
            addAriaLabel={t("historyForm.actions.add")}
          >
            <div className="hf-table hf-table--allergy">
              <div className="hf-th">
                <span>{t("historyForm.allergy.headers.type")}</span>
                <span>{t("historyForm.allergy.headers.reaction")}</span>
                <span>{t("historyForm.allergy.headers.severity")}</span>
                <span>{t("historyForm.common.remove")}</span>
              </div>

              {allergies.map((a) => (
                <div key={a.id} className={`hf-tr ${a.isNew ? "is-new" : ""}`}>
                  <div className="hf-td hf-allergy-kind">
                    <select
                      className="hf-allergy-category"
                      value={a.category || "DRUG"}
                      disabled={a.model === "surgicalHistory"}
                      onChange={(e) => updateAllergy(a.id, { category: e.target.value })}
                      aria-label={t("historyForm.allergy.headers.category")}
                    >
                      <option value="DRUG">{t("historyForm.allergy.categories.drug")}</option>
                      <option value="FOOD">{t("historyForm.allergy.categories.food")}</option>
                      <option value="ENVIRONMENTAL">{t("historyForm.allergy.categories.environmental")}</option>
                    </select>
                    <input
                      className="hf-input hf-input--editable"
                      value={a.name}
                      placeholder={t(`historyForm.allergy.placeholders.${String(a.category || "DRUG").toLowerCase()}`)}
                      onChange={(e) => updateAllergy(a.id, { name: e.target.value })}
                    />
                  </div>

                  <div className="hf-td">
                    <input
                      className="hf-input hf-input--editable"
                      value={a.reactionDescription || ""}
                      disabled={a.category !== "DRUG"}
                      placeholder={a.category === "DRUG" ? t("historyForm.allergy.placeholders.reaction") : "—"}
                      onChange={(e) => updateAllergy(a.id, { reactionDescription: e.target.value })}
                    />
                  </div>

                  <div className="hf-td">
                    <div className={`hf-sel hf-sel--${toneFromValue(a.severityLevel)}`}>
                      <select
                        className="hf-select"
                        value={a.severityLevel || "MILD"}
                        disabled={a.model === "surgicalHistory"}
                        onChange={(e) => updateAllergy(a.id, { severityLevel: e.target.value })}
                      >
                        <option value="MILD">{t("historyForm.severity.mild")}</option>
                        <option value="MODERATE">{t("historyForm.severity.moderate")}</option>
                        <option value="SEVERE">{t("historyForm.severity.severe")}</option>
                        <option value="LIFE_THREATENING">{t("historyForm.severity.lifeThreatening")}</option>
                      </select>
                    </div>
                  </div>

                  <div className="hf-td hf-td--end">
                    <button
                      type="button"
                      className="hf-iconbtn hf-iconbtn--ghost"
                      onClick={() => removeAllergy(a.id)}
                      aria-label={t("historyForm.actions.remove")}
                    >
                      <TrashIcon />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </HfCard>
        </div>
      </div>

      {/* SURGERY */}
      <div className="hf-row">
        <div className="hf-leftside">
          <h3 className="hf-bullet">{t("historyForm.surgery.leftTitle")}</h3>
          <p className="hf-desc">{t("historyForm.surgery.leftDesc")}</p>
        </div>
        <div className="hf-rightside">
          <HfCard
            icon={<SurgicalScalpelIcon />}
            title={t("historyForm.surgery.cardTitle")}
            onAdd={addSurgery}
            addAriaLabel={t("historyForm.actions.add")}
          >
            <div className="hf-table">
              <div className="hf-th">
                <span>{t("historyForm.surgery.headers.type")}</span>
                <span>{t("historyForm.surgery.headers.year")}</span>
                <span>{t("historyForm.common.remove")}</span>
              </div>

              {surgeries.map((s) => (
                <div key={s.id} className={`hf-tr ${s.isNew ? "is-new" : ""}`}>
                  <div className="hf-td">
                    <span className="hf-dot" />
                    <input
                      className="hf-input hf-input--editable"
                      value={s.type}
                      onChange={(e) => updateSurgery(s.id, { type: e.target.value })}
                    />
                  </div>

                  <div className="hf-td">
                    <input
                      className="hf-input hf-input--editable"
                      value={s.year}
                      onChange={(e) => updateSurgery(s.id, { year: e.target.value })}
                    />
                  </div>

                  <div className="hf-td hf-td--end">
                    <button
                      type="button"
                      className="hf-iconbtn hf-iconbtn--ghost"
                      onClick={() => removeSurgery(s.id)}
                      aria-label={t("historyForm.actions.remove")}
                    >
                      <TrashIcon />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </HfCard>
        </div>
      </div>

      {/* FAMILY */}
      <div className="hf-row">
        <div className="hf-leftside">
          <h3 className="hf-bullet">{t("historyForm.family.leftTitle")}</h3>
          <p className="hf-desc">{t("historyForm.family.leftDesc")}</p>
        </div>
        <div className="hf-rightside">
          <HfCard
            icon={<MedicalFamilyHistoryIcon />}
            title={t("historyForm.family.cardTitle")}
            onAdd={addFamily}
            addAriaLabel={t("historyForm.actions.add")}
          >
            <div className="hf-table">
              <div className="hf-th">
                <span>{t("historyForm.family.headers.condition")}</span>
                <span>{t("historyForm.family.headers.relation")}</span>
                <span>{t("historyForm.common.remove")}</span>
              </div>

              {familyHistory.map((f) => (
                <div key={f.id} className={`hf-tr ${f.isNew ? "is-new" : ""}`}>
                  <div className="hf-td">
                    <span className="hf-dot" />
                    <input
                      className="hf-input hf-input--editable"
                      value={f.condition}
                      onChange={(e) => updateFamily(f.id, { condition: e.target.value })}
                    />
                  </div>

                  <div className="hf-td">
                    <input
                      className="hf-input hf-input--editable"
                      value={f.relation}
                      onChange={(e) => updateFamily(f.id, { relation: e.target.value })}
                    />
                  </div>

                  <div className="hf-td hf-td--end">
                    <button
                      type="button"
                      className="hf-iconbtn hf-iconbtn--ghost"
                      onClick={() => removeFamily(f.id)}
                      aria-label={t("historyForm.actions.remove")}
                    >
                      <TrashIcon />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </HfCard>
        </div>
      </div>

      {/* CHRONIC */}
      <div className="hf-row">
        <div className="hf-leftside">
          <h3 className="hf-bullet">{t("historyForm.chronic.leftTitle")}</h3>
          <p className="hf-desc">{t("historyForm.chronic.leftDesc")}</p>
        </div>
        <div className="hf-rightside">
          <HfCard
            icon={<ChronicConditionsIcon />}
            title={t("historyForm.chronic.cardTitle")}
            onAdd={addChronic}
            addAriaLabel={t("historyForm.actions.add")}
          >
            <div className="hf-table">
              <div className="hf-th">
                <span>{t("historyForm.chronic.headers.condition")}</span>
                <span>{t("historyForm.chronic.headers.year")}</span>
                <span>{t("historyForm.common.remove")}</span>
              </div>

              {chronic.map((c) => (
                <div key={c.id} className={`hf-tr ${c.isNew ? "is-new" : ""}`}>
                  <div className="hf-td">
                    <span className="hf-dot" />
                    <input
                      className="hf-input hf-input--editable"
                      value={c.condition}
                      onChange={(e) => updateChronic(c.id, { condition: e.target.value })}
                    />
                  </div>

                  <div className="hf-td">
                    <input
                      className="hf-input hf-input--editable"
                      value={c.diagnosis}
                      onChange={(e) => updateChronic(c.id, { diagnosis: e.target.value })}
                    />
                  </div>

                  <div className="hf-td hf-td--end">
                    <button
                      type="button"
                      className="hf-iconbtn hf-iconbtn--ghost"
                      onClick={() => removeChronic(c.id)}
                      aria-label={t("historyForm.actions.remove")}
                    >
                      <TrashIcon />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </HfCard>
        </div>
      </div>
    </div>
  );
}
